




































































































































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import { Location } from "vue-router";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import Pagination from "@/shared/components/ui/Pagination.vue";
import CancelConfirmationModal from "@/shared/components/contributionsV3/CancelConfirmationModal.vue";
import {
  TransferSchedule,
  TransferScheduleType,
  TransferScheduleStatus,
  TransferScheduleOneTimeDetails
} from "@/shared/types";
import { formatCurrency } from "@/shared/currency";
import moment from "moment";
import { getScheduleDescription } from "@/shared/components/contributionsV3/scheduleDescriptions";
import { useBankAccountStore } from "@/employee/store/bankAccount";
import { useNotifierStore } from "@/shared/store/notifier";
import { usePersonalContributionSchedulesStore } from "@/shared/store/personalContributionSchedules";
import _ from "lodash";
import Pill from "@/shared/components/ui/Pill.vue";
import { isScheduleCompleted } from "@/shared/components/contributionsV3/scheduleStatusHelper";

const frequencyCaptions = {
  [TransferScheduleType.None] : "",
  [TransferScheduleType.MonthlyByDate]: "Monthly",
  [TransferScheduleType.MonthlyByDay]: "Monthly",
  [TransferScheduleType.Weekly]: "Weekly",
  [TransferScheduleType.OneTime]: "One-time",
};

interface ScheduleRow {
  scheduleId: number;
  scheduleStartDate: string;
  scheduleEndDate?: string;
  cancelledDate?: string;
  cancelledBy: string;
  scheduleInterval: number;
  linkedExternalBankAccountId: number;
  scheduleType: TransferScheduleType;
  amount: number;
  description: string;
  _showDetails: boolean;
  status: TransferScheduleStatus;
  upcomingDates?: string[];
  nextDate?: string;
  oneTimeDetails?: TransferScheduleOneTimeDetails[];
  isUnlocked: boolean;
  taxYear: number;
}

@Component ({
  components: {
    Pill,
    GenericLoadingSpinner,
    Pagination,
    CancelConfirmationModal
  }
})
export default class TransferSchedulesTable extends Vue {

  bankAccountStore = useBankAccountStore();
  notifierStore = useNotifierStore();
  contributionSchedulesStore = usePersonalContributionSchedulesStore();
  isScheduleCompleted = isScheduleCompleted;

  frequencyCaptions = frequencyCaptions;
  formatCurrency = formatCurrency;
  cancelModalVisible = false;
  scheduleToCancel: TransferSchedule = null;
  tableRows: ScheduleRow[] = [];

  allFields = [
    {
      key: "showDetails",
      label: ""
    },
    {
      key: "scheduleStartDate",
      label: "Start Date",
      thClass: "d-none d-md-table-cell",
      tdClass: "d-none d-md-table-cell"
    },
    {
      key: "scheduleEndDate",
      label: "End Date",
      thClass: "d-none d-md-table-cell",
      tdClass: "d-none d-md-table-cell"
    },
    {
      key: "nextDate",
      label: "Next Scheduled Date",
    },
    {
      key: "scheduleInterval",
      label: "Frequency",
      thClass: "d-none d-md-table-cell",
      tdClass: "d-none d-md-table-cell"
    },
    {
      key: "amount"
    },
    {
      key: "status",
    },
    {
      key: "actions",
      thClass: "d-none d-md-table-cell",
      tdClass: "d-none d-md-table-cell"
    }
  ];

  @Prop()
  schedules: TransferSchedule[];

  @Prop()
  loaded: boolean;

  @Prop()
  page: number;

  @Prop()
  pageCount: number;

  @Prop({required: false})
  routeToModifySchedule: (id: number) => Location;

  @Prop()
  distributionsMode: boolean;

  @Prop({required: false})
  username: string;

  @Prop({required: false})
  partnerId: number;

  @Prop()
  readOnly: boolean;

  @Prop()
  cancelOnly: boolean;

  @Prop({default: false})
  displayCancelationDetails: boolean;

  mounted () {
    if (this.distributionsMode) {
      this.allFields = this.allFields.filter(f => f.key !== "actions" && f.key !== "nextDate");
    }
    if (this.readOnly) {
      this.allFields = this.allFields.filter(f => f.key !== "actions");
    }
  }

  @Watch("schedules", { immediate: true, deep: true })
  @Watch("loaded")
  @Watch("username")
  @Watch("PartnerId")
  updateRows () {
    this.tableRows = this.buildTableRows();
  }

  get isTableEmpty () {
    return this.loaded && this.tableRows.length === 0;
  }

  get tableFields () {
    return this.allFields;
  }

  get currentYear (): number {
    return moment().year();
  }

  get displayModify () {
    return !this.cancelOnly;
  }

  buildTableRows (): ScheduleRow[] {
    if (!this.loaded) {
      return [];
    }

    return this.schedules.map(x => {
      return {
        scheduleId: x.scheduleId,
        scheduleStartDate: x.scheduleStartDate,
        scheduleEndDate: x.scheduleType === TransferScheduleType.OneTime ? x.scheduleStartDate : x.scheduleEndDate,
        status: x.status,
        // Show the end date as cancelled date if this schedule has expired
        cancelledDate: x.status === TransferScheduleStatus.Cancelled ? x.dateLastModified : null,
        cancelledBy: x.cancelledBy,
        scheduleInterval: x.scheduleInterval,
        linkedExternalBankAccountId: x.linkedExternalBankAccountId,
        scheduleType: x.scheduleType,
        amount: x.amount,
        description: getScheduleDescription(x),
        _showDetails: false,
        nextDate: x.nextDate,
        upcomingDates: x.upcomingDates,
        oneTimeDetails: x.oneTimeDetails,
        isUnlocked: !x.isLocked,
        taxYear: this.getTaxYear(x)
      };
    });
  }

  isScheduleCancelled (row: ScheduleRow) {
    return row.status === TransferScheduleStatus.Cancelled;
  }

  isScheduleActive (row: ScheduleRow) {
    return row.status === TransferScheduleStatus.Active;
  }

  isOneTimeContribution (scheduleType: string|TransferScheduleType) {
    return (scheduleType === TransferScheduleType.OneTime);
  }

  getFormattedDate (date?: string): string {
    return date && moment(date).format("MMM D, YYYY") || "None";
  }

  getEndDate (row: ScheduleRow): string {
    if (row.status === TransferScheduleStatus.Active || row.status === TransferScheduleStatus.Completed) {
      return this.getFormattedDate(row.scheduleEndDate);
    }
    return this.getFormattedDate(row.cancelledDate);
  }

  getAccountName (accountId: number): string {
    const index = this.bankAccountStore.verifiedLinkedAccounts.findIndex(x => x.linkedBankAccountId === accountId);
    if (index < 0) {
      return accountId.toString();
    }

    return this.bankAccountStore.verifiedLinkedAccounts[index].bankAccountName;
  }

  isPriorYearContribution (schedule: TransferSchedule): boolean {
    return this.isOneTimeContribution(schedule.scheduleType) && (schedule.oneTimeDetails || []).some(x => x.isPriorYearContribution);
  }

  getTaxYear (schedule: TransferSchedule): number|undefined {
    if (this.isOneTimeContribution(schedule.scheduleType)) {
      const year = moment(schedule.scheduleStartDate).year();
      const offset = this.isPriorYearContribution(schedule) ? 1 : 0;
      return year - offset;
    }
  }

  cancelSchedule (row: ScheduleRow) {
    const schedule = this.schedules.find(
      x => x.scheduleId === row.scheduleId);
    this.scheduleToCancel = _.cloneDeep(schedule);
    this.cancelModalVisible = true;
  }

  modifySchedule (row: ScheduleRow) {
    this.$router.push(this.routeToModifySchedule(row.scheduleId));
  }

  onCancelCancellation () {
    this.cancelModalVisible = false;
    this.scheduleToCancel = null;
  }

  onSubmitCancellation () {
    this.scheduleToCancel.status = TransferScheduleStatus.Cancelled;
    const taxYear = this.getTaxYear(this.scheduleToCancel) || this.currentYear;

    this.contributionSchedulesStore.cancelPersonalContributionSchedule(
      this.scheduleToCancel.scheduleId, this.username, this.partnerId)
    .then(_ => {
      this.notifierStore.setSuccess("Your personal contribution schedule has been cancelled");
      this.contributionSchedulesStore.resetSchedulesFetchState();
      this.contributionSchedulesStore.getUpcomingPersonalContributionTotal(taxYear);
    })
    .catch(_ => {
      this.notifierStore.setDanger("An error occurred while cancelling this schedule.");
    })
    .finally(() => {
      this.cancelModalVisible = false;
      this.scheduleToCancel = null;
    });
  }

  onRowSelected (selectedRows: ScheduleRow[]) {
    for (const schedule of this.tableRows) {
      schedule._showDetails =
        (selectedRows.findIndex(x => x.scheduleId === schedule.scheduleId) >= 0);
    }
  }

  getRowClasses (item: ScheduleRow, type: string): string {
    if (type === "row") {
      return item._showDetails ? "row-selected" : "";
    }

    if (type === "row-details") {
      return "child-container";
    }

    return "";
  }

  deselectFromDetail (index: number) {
    (this.$refs.scheduleTable as any).unselectRow(index);
  }

  switchPage (newPage) {
    this.$router.push({query: {...this.$route.query, schedulesPage: newPage}});
  }

  getUpcomingText (): string {
    if (this.distributionsMode) {
      return "Upcoming Distributions:";
    }
    return "Upcoming Contributions:";
  }

  showModifyAndCancelButtons (status) {
    return (!this.readOnly && status === TransferScheduleStatus.Active);
  }

  disableButtons (status, isUnlocked) {
    return !(status === TransferScheduleStatus.Active && isUnlocked);
  }
}
